import { useEffect, useState } from "react";
import { Calendar } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { PrimaryButton } from "../Button/PrimaryButton";
import SecondaryButton from "../Button/SecondaryButton";

const CALENDAR_CLASS_NAMES = {
  base: "border-none shadow-none bg-white dark:bg-gray-900",
  content: "bg-white dark:bg-gray-900",
  header: "bg-white dark:bg-gray-900",
  headerWrapper: "pt-0 bg-white dark:bg-gray-900",
  gridWrapper: "bg-white dark:bg-gray-900",
  gridHeader: "bg-white dark:bg-gray-900",
  gridHeaderRow: "bg-white dark:bg-gray-900",
  gridBody: "bg-white dark:bg-gray-900",
  grid: "bg-white dark:bg-gray-900",
  table: "bg-white dark:bg-gray-900 border-collapse",
  title: "text-secondary dark:text-secondary-light font-bold text-sm",
  gridHeaderCell: "text-secondary dark:text-secondary-light text-xs font-bold bg-white dark:bg-gray-900",
  cellButton: [
    "text-gray-900 dark:text-gray-100 font-medium text-sm",
    "data-[outside-month=true]:text-gray-400 dark:data-[outside-month=true]:text-gray-600",
    "data-[today=true]:font-bold data-[today=true]:text-secondary dark:data-[today=true]:text-secondary-light data-[today=true]:bg-secondary/15 dark:data-[today=true]:bg-secondary/25 data-[today=true]:ring-2 data-[today=true]:ring-secondary/70",
    "data-[outside-month=true]:data-[today=true]:ring-0 data-[outside-month=true]:data-[today=true]:bg-transparent data-[outside-month=true]:data-[today=true]:font-normal data-[outside-month=true]:data-[today=true]:text-gray-400 dark:data-[outside-month=true]:data-[today=true]:text-gray-600",
    "data-[selected=true]:!bg-secondary data-[selected=true]:!text-white dark:data-[selected=true]:!text-white dark:data-[selected=true]:!bg-secondary data-[selected=true]:!ring-0",
    "data-[hover=true]:bg-secondary/15 dark:data-[hover=true]:bg-secondary/30",
    "data-[hover=true]:text-gray-900 dark:data-[hover=true]:text-white",
    "data-[selected=true]:data-[hover=true]:!bg-secondary",
    "data-[selected=true]:data-[hover=true]:!text-white",
    "data-[focus-visible=true]:outline-secondary",
    "data-[focus-visible=true]:outline-offset-0",
  ],
  prevButton: "text-secondary dark:text-secondary-light hover:bg-gray-100 dark:hover:bg-gray-800",
  nextButton: "text-secondary dark:text-secondary-light hover:bg-gray-100 dark:hover:bg-gray-800",
};

const toDateString = (date) =>
  `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;

const parseCalendarValue = (value) => {
  if (!value) return null;

  try {
    if (typeof value === "string") {
      return parseDate(value);
    }

    return parseDate(value.toISOString().split("T")[0]);
  } catch {
    return null;
  }
};

const CalendarPopoverPanel = ({
  value = null,
  onConfirm,
  onCancel,
  minDate = null,
  maxDate = null,
}) => {
  const [pendingDate, setPendingDate] = useState(() =>
    parseCalendarValue(value),
  );

  useEffect(() => {
    setPendingDate(parseCalendarValue(value));
  }, [value]);

  const handleDone = () => {
    if (pendingDate && onConfirm) {
      onConfirm(toDateString(pendingDate));
    } else if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="border pt-4 border-gray-200 dark:border-gray-800 rounded-xl bg-white dark:bg-gray-900 w-fit">
      <Calendar
        aria-label="Select date"
        color="secondary"
        value={pendingDate}
        onChange={setPendingDate}
        minValue={minDate ? parseDate(minDate) : undefined}
        maxValue={maxDate ? parseDate(maxDate) : undefined}
        classNames={CALENDAR_CLASS_NAMES}
      />

      <div className="flex items-center justify-between px-4 pb-4">
        <SecondaryButton
          title="Cancel"
          onClick={onCancel}
          className="shadow-none border-none text-gray-800 dark:text-gray-200"
        />

        <PrimaryButton
          size="sm"
          className="bg-secondary text-white text-xs font-medium rounded-lg !px-3"
          onClick={handleDone}
          disabled={!pendingDate}
          startContent={false}
        >
          Done
        </PrimaryButton>
      </div>
    </div>
  );
};

export default CalendarPopoverPanel;
